import { useEffect, useState } from "react";
import { Box, Typography, Card, CardContent, Grid } from "@mui/material";
import InsightsIcon from "@mui/icons-material/Insights";
import { api } from "../services/api";
import AppLoader from "../components/AppLoader";
import { Link, useNavigate } from "react-router-dom";

const AdminDashboard = () => {
  const [data, setData] = useState(null);
  const navigate = useNavigate();

  const formatShortDateTime = (dateStr) => {
    if (!dateStr) return "-";
    const date = new Date(dateStr);
    return `${date.getDate()}/${date.getMonth() + 1}/${String(date.getFullYear()).slice(-2)} ${String(date.getHours()).padStart(2, "0")}:${String(date.getMinutes()).padStart(2, "0")}`;
  };

  const formatNumber = (value) => {
    const numericValue = Number(value || 0);
    return numericValue.toLocaleString("en-US");
  };

  const formatInteger = (value) => {
    const numericValue = Number(value || 0);
    return numericValue.toLocaleString("en-US", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    });
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await api.get("/api/admin/dashboard");
        setData(res.data);
      } catch (err) {
        console.error("Error fetching admin dashboard:", err);
      }
    };
    fetchData();
  }, []);

  if (!data) {
    return <AppLoader message="Loading admin dashboard..." />;
  }

  const aiConversationUsage = data.ai_conversation || {
    total_sessions: 0,
    total_words: 0,
    items: [],
  };

  const CardBlock = ({ title, value, subtitle1, subtitle2, color = "#1c1c1c" }) => (
    <Card sx={{ backgroundColor: color, color: "#fff", display: "flex", alignItems: "center" }}>
      <CardContent sx={{ flexGrow: 1 }}>
        <Typography variant="body2">{title}</Typography>
        <Typography variant="h6">{value}</Typography>
        {subtitle1 && <Typography variant="body2" sx={{ mt: 1 }}>{subtitle1}</Typography>}
        {subtitle2 && <Typography variant="body2">{subtitle2}</Typography>}
      </CardContent>
      {color !== "#1c1c1c" && (
        <Box sx={{ pr: 2 }}>
          <InsightsIcon fontSize="large" />
        </Box>
      )}
    </Card>
  );

  const Section = ({ title, summary, items }) => (
    <Box mb={4}>
      <Typography variant="h6" gutterBottom>{title}</Typography>
      <Grid
        container
        spacing={2}
        sx={{
          display: "flex",
          flexWrap: "wrap",
          flexDirection: { xs: "column", md: "row" },
        }}
      >
        {summary && (
          <Grid item xs={12} md={3}>
            <CardBlock
              title={summary.title || title}
              value={summary.sessions}
              subtitle1={summary.words ? `Total Words: ${summary.words}` : null}
              subtitle2={
                !isNaN(Number(summary.avg))
                  ? `Avg per Session: ${Number(summary.avg).toFixed(0)}`
                  : null
              }
              color="#4a148c"
            />
          </Grid>
        )}

        {items.map(({ label, value, words, avg }, index) => (
          <Grid item xs={12} md={3} key={index}>
            <CardBlock
              title={label}
              value={value}
              subtitle1={words ? `Words: ${words}` : null}
              subtitle2={
                !isNaN(Number(avg))
                  ? `Avg: ${Number(avg).toFixed(0)}`
                  : null
              }
            />
          </Grid>
        ))}
      </Grid>
    </Box>
  );

  const SummaryStatsCard = ({ title, totalLabel, totalValue, stats = [], links = [], onClick }) => (
    <Card
      sx={{
        backgroundColor: "rgba(56, 24, 69, 0.96)",
        color: "#fff",
        border: "1px solid rgba(255,255,255,0.08)",
        borderRadius: 3,
        boxShadow: "none",
        height: "100%",
        cursor: onClick ? "pointer" : "default",
        transition: "transform 0.16s ease, border-color 0.16s ease, background-color 0.16s ease",
        "&:hover": onClick ? {
          transform: "translateY(-2px)",
          borderColor: "rgba(144,202,249,0.38)",
          backgroundColor: "rgba(63, 28, 78, 0.98)",
        } : {},
      }}
      onClick={onClick}
    >
      <CardContent sx={{ p: 2.2, "&:last-child": { pb: 2.2 } }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2, mb: 1.5 }}>
          <Typography variant="h6" sx={{ fontSize: 20, fontWeight: 700 }}>
            {title}
          </Typography>
          {!!links.length && (
            <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", justifyContent: "flex-end" }}>
              {links.map((link) => (
                <Link
                  key={link.to}
                  to={link.to}
                  onClick={(event) => event.stopPropagation()}
                  onMouseEnter={(event) => {
                    event.currentTarget.style.color = "#c7e3ff";
                  }}
                  onMouseLeave={(event) => {
                    event.currentTarget.style.color = "#90caf9";
                  }}
                  style={{
                    fontSize: 13,
                    color: "#90caf9",
                    fontWeight: "bold",
                    textDecoration: "none",
                    whiteSpace: "nowrap",
                  }}
                >
                  {link.label}
                </Link>
              ))}
            </Box>
          )}
        </Box>

        <Grid container spacing={1} sx={{ mb: 1.5 }}>
          <Grid item xs={12} sm={6}>
            <Box
              sx={{
                height: "100%",
                px: 1.4,
                py: 0.9,
                borderRadius: 2,
                background: "linear-gradient(135deg, rgba(116,58,168,0.92), rgba(74,20,140,0.92))",
                border: "1px solid rgba(171,130,255,0.35)",
                minHeight: 64,
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
              }}
            >
              <Typography sx={{ fontSize: 11, color: "#eadcff", lineHeight: 1 }}>
                {totalLabel}
              </Typography>
              <Typography sx={{ fontSize: 24, fontWeight: 800, lineHeight: 1.05, mt: 0.45 }}>
                {totalValue}
              </Typography>
            </Box>
          </Grid>

          {stats[0] && (
            <Grid item xs={12} sm={6}>
              <Box
                sx={{
                  height: "100%",
                  px: 1.2,
                  py: 0.9,
                  borderRadius: 2,
                  backgroundColor: "rgba(255,255,255,0.05)",
                  border: "1px solid rgba(255,255,255,0.08)",
                  minHeight: 64,
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                }}
              >
                <Typography sx={{ fontSize: 11, color: "#c9b6d3", lineHeight: 1 }}>
                  {stats[0].label}
                </Typography>
                <Typography sx={{ fontSize: 22, fontWeight: 700, lineHeight: 1.15, mt: 0.45 }}>
                  {stats[0].value}
                </Typography>
              </Box>
            </Grid>
          )}
        </Grid>

        <Grid container spacing={1}>
          {stats.slice(1).map((stat) => (
            <Grid item xs={12} sm={6} key={stat.label}>
              <Box
                sx={{
                  height: "100%",
                px: 1.2,
                py: 0.9,
                borderRadius: 2,
                backgroundColor: "rgba(255,255,255,0.05)",
                border: "1px solid rgba(255,255,255,0.08)",
                minHeight: 64,
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
              }}
            >
                <Typography sx={{ fontSize: 11, color: "#c9b6d3", lineHeight: 1 }}>
                  {stat.label}
                </Typography>
                <Typography sx={{ fontSize: 20, fontWeight: 700, lineHeight: 1.15, mt: 0.4 }}>
                  {stat.value}
                </Typography>
              </Box>
            </Grid>
          ))}
        </Grid>

      </CardContent>
    </Card>
  );

  const ModelUsageCard = ({ title, totalSessions, totalWords, items = [], wordLabel = "Total Words" }) => (
    <Card
      sx={{
        backgroundColor: "rgba(56, 24, 69, 0.96)",
        color: "#fff",
        border: "1px solid rgba(255,255,255,0.08)",
        borderRadius: 3,
        boxShadow: "none",
        height: "100%",
      }}
    >
      <CardContent sx={{ p: 2.2, "&:last-child": { pb: 2.2 } }}>
        <Typography variant="h6" sx={{ fontSize: 20, fontWeight: 700, mb: 1.5 }}>
          {title}
        </Typography>

        <Grid container spacing={1} sx={{ mb: 1.5 }}>
          <Grid item xs={6}>
            <Box
              sx={{
                px: 1.2,
                py: 0.9,
                borderRadius: 2,
                background: "linear-gradient(135deg, rgba(116,58,168,0.92), rgba(74,20,140,0.92))",
                border: "1px solid rgba(171,130,255,0.35)",
                minHeight: 64,
              }}
            >
              <Typography sx={{ fontSize: 11, color: "#eadcff", lineHeight: 1 }}>
                Total Sessions
              </Typography>
              <Typography sx={{ fontSize: 24, fontWeight: 800, lineHeight: 1.05, mt: 0.45 }}>
                {formatNumber(totalSessions)}
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={6}>
            <Box
              sx={{
                px: 1.2,
                py: 0.9,
                borderRadius: 2,
                backgroundColor: "rgba(255,255,255,0.05)",
                border: "1px solid rgba(255,255,255,0.08)",
                minHeight: 64,
              }}
            >
              <Typography sx={{ fontSize: 11, color: "#c9b6d3", lineHeight: 1 }}>
                {wordLabel}
              </Typography>
              <Typography sx={{ fontSize: 22, fontWeight: 700, lineHeight: 1.05, mt: 0.45 }}>
                {formatNumber(totalWords)}
              </Typography>
            </Box>
          </Grid>
        </Grid>

        <Box sx={{ display: "grid", gap: 1 }}>
          {items.map((item) => (
            <Box
              key={`${title}-${item.label}`}
              sx={{
                px: 1.2,
                py: 1,
                borderRadius: 2,
                backgroundColor: "rgba(255,255,255,0.05)",
                border: "1px solid rgba(255,255,255,0.08)",
              }}
            >
              <Typography sx={{ fontSize: 14, fontWeight: 700, color: "#fff" }}>
                {item.label}
              </Typography>
              <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", mt: 0.6 }}>
                <Typography sx={{ fontSize: 12, color: "#c9b6d3" }}>
                  Sessions: <strong style={{ color: "#fff" }}>{formatNumber(item.sessions)}</strong>
                </Typography>
                <Typography sx={{ fontSize: 12, color: "#c9b6d3" }}>
                  Words: <strong style={{ color: "#fff" }}>{formatNumber(item.words)}</strong>
                </Typography>
                <Typography sx={{ fontSize: 12, color: "#c9b6d3" }}>
                  Avg: <strong style={{ color: "#fff" }}>{formatInteger(item.avg)}</strong>
                </Typography>
                <Typography sx={{ fontSize: 12, color: "#c9b6d3" }}>
                  Last Usage: <strong style={{ color: "#fff" }}>{item.lastUsage}</strong>
                </Typography>
              </Box>
            </Box>
          ))}
        </Box>
      </CardContent>
    </Card>
  );

  const quickAccessItems = [
    {
      title: "📬 Contact Messages",
      lines: [
        `Total: ${data.contact_messages?.total ?? 0}`,
        `New: ${data.contact_messages?.new ?? 0}`,
      ],
      link: "/admin/contact-messages",
      linkLabel: "View Messages",
    },
    {
      title: "🚀 Early Pitchers",
      lines: [
        `Total: ${data.early_pitchers?.total ?? 0}`,
        `Pending: ${data.early_pitchers?.pending ?? 0}`,
      ],
      link: "/admin/early-pitchers",
      linkLabel: "View Applications",
    },
    {
      title: "💳 Purchases",
      lines: [
        `Total: ${data.purchases?.total ?? 0}`,
        `Paid: ${data.purchases?.paid ?? 0}`,
        `Cancelled: ${data.purchases?.cancelled ?? 0}`,
        `Pending: ${data.purchases?.pending ?? 0}`,
      ],
      link: "/admin/purchases",
      linkLabel: "View Purchases",
    },
    {
      title: "💸 Payments",
      lines: [
        `Total: ${data.payments?.total ?? 0}`,
        `Completed: ${data.payments?.completed ?? 0}`,
        `Refunded: ${data.payments?.refunded ?? 0}`,
        `Failed: ${data.payments?.failed ?? 0}`,
      ],
      link: "/admin/payments",
      linkLabel: "View Payments",
    },
    {
      title: "🎟️ Discount Assignments",
      lines: [
        `Total: ${data.discount_assignments ?? 0}`,
      ],
      link: "/admin/discount-assignments",
      linkLabel: "View Discount Assignments",
    },
    {
      title: "⚙️ Settings",
      lines: [
        `App Settings: ${data.settings?.app ?? 0}`,
        `User Settings: ${data.settings?.user ?? 0}`,
      ],
      link: "/admin/settings",
      linkLabel: "View Settings Admin",
    },
  ];

  const QuickAccessCard = ({ item }) => (
    <Card
      onClick={() => navigate(item.link)}
      sx={{
        backgroundColor: "rgba(56, 24, 69, 0.96)",
        color: "#fff",
        height: "100%",
        border: "1px solid rgba(255,255,255,0.08)",
        borderRadius: 3,
        boxShadow: "none",
        cursor: "pointer",
        transition: "transform 0.16s ease, border-color 0.16s ease, background-color 0.16s ease",
        "&:hover": {
          transform: "translateY(-2px)",
          borderColor: "rgba(144,202,249,0.38)",
          backgroundColor: "rgba(63, 28, 78, 0.98)",
        },
      }}
    >
      <CardContent
        sx={{
          p: 2,
          "&:last-child": { pb: 2 },
          display: "flex",
          flexDirection: "column",
          gap: 1.5,
        }}
      >
        <Typography variant="h6" sx={{ fontSize: 18, fontWeight: 700, lineHeight: 1.2 }}>
          {item.title}
        </Typography>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
            gap: 1,
            alignItems: "stretch",
          }}
        >
          {item.lines.map((line, index) => {
            const [label, value] = line.split(": ");
            return (
              <Box
                key={`${item.title}-${index}`}
                sx={{
                  px: 1.2,
                  py: 0.8,
                  borderRadius: 2,
                  backgroundColor: "rgba(255,255,255,0.05)",
                  border: "1px solid rgba(255,255,255,0.08)",
                  minHeight: 56,
                }}
              >
                <Typography sx={{ fontSize: 11, color: "#c9b6d3", lineHeight: 1 }}>
                  {label}
                </Typography>
                <Typography sx={{ fontSize: 20, fontWeight: 700, color: "#fff", lineHeight: 1.15, mt: 0.4 }}>
                  {value}
                </Typography>
              </Box>
            );
          })}
        </Box>

      </CardContent>
    </Card>
  );

  return (
    <Box
      sx={{
        backgroundImage: 'url("/assets/bg-pitchest.jpg")',
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
        minHeight: "100vh",
        px: 2,
        py: 6,
      }}
    >
      <Box
        sx={{
          backgroundColor: "rgba(0, 0, 0, 0.6)",
          borderRadius: 4,
          padding: { xs: 2, md: 4 },
          color: "white",
        }}
      >
        <Typography variant="h5" gutterBottom color="white">Admin Dashboard</Typography>

        <Box mb={4}>
          <Typography variant="h6" gutterBottom>Quick Access</Typography>
          <Grid container spacing={2}>
            {quickAccessItems.map((item) => (
              <Grid item xs={12} sm={6} md={4} lg={2.4} key={item.title}>
                <QuickAccessCard item={item} />
              </Grid>
            ))}
          </Grid>
        </Box>

        <Box mb={4}>
          <Grid container spacing={2}>
            <Grid item xs={12} lg={6}>
              <SummaryStatsCard
                title="👥 Users"
                totalLabel="Total Users"
                totalValue={data.users.total}
                stats={[
                  { label: "Today", value: data.users.today },
                  { label: "Yesterday", value: data.users.yesterday },
                  { label: "Last 7 days", value: data.users.week },
                ]}
                onClick={() => navigate("/admin/users")}
              />
            </Grid>
            <Grid item xs={12} lg={6}>
              <SummaryStatsCard
                title="💬 Sessions"
                totalLabel="Total With Evaluation"
                totalValue={data.sessions.with_evaluation.total}
                stats={[
                  { label: "Today (WE)", value: data.sessions.with_evaluation.today },
                  { label: "Yesterday (WE)", value: data.sessions.with_evaluation.yesterday },
                  { label: "Last 7 days (WE)", value: data.sessions.with_evaluation.week },
                ]}
                onClick={() => navigate("/admin/sessions")}
                links={[
                  { to: "/admin/session-refunds", label: `💸 View Auto-Refunds (${data?.session_refunds?.total || 0})` },
                ]}
              />
            </Grid>
          </Grid>
        </Box>
        <Box mb={4}>
          <Typography variant="h6" gutterBottom>Model Usage</Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} lg={4}>
              <ModelUsageCard
                title="🤖 LLM"
                totalSessions={aiConversationUsage.total_sessions || 0}
                totalWords={aiConversationUsage.total_words || 0}
                items={(aiConversationUsage.items || []).map((item) => ({
                  label: item.provider || item.model,
                  sessions: item.sessions,
                  words: item.ai_words,
                  avg: item.avg_words_per_session,
                  lastUsage: formatShortDateTime(item.last_usage_at),
                }))}
              />
            </Grid>
            <Grid item xs={12} lg={4}>
              <ModelUsageCard
                title="🎙️ STT"
                totalSessions={data.stt.total_sessions}
                totalWords={data.stt.total_words}
                items={data.stt.items.map((item) => ({
                  label: item.engine,
                  sessions: item.sessions,
                  words: item.user_words,
                  avg: item.avg_words_per_session,
                  lastUsage: formatShortDateTime(item.last_usage_at),
                }))}
              />
            </Grid>
            <Grid item xs={12} lg={4}>
              <ModelUsageCard
                title="🔊 TTS"
                totalSessions={data.tts.total_sessions}
                totalWords={data.tts.total_words}
                items={data.tts.items.map((item) => ({
                  label: item.engine,
                  sessions: item.sessions,
                  words: item.ai_words,
                  avg: item.avg_words_per_session,
                  lastUsage: formatShortDateTime(item.last_usage_at),
                }))}
              />
            </Grid>
          </Grid>
        </Box>
      </Box>
    </Box>
  );
};

export default AdminDashboard;
